import { FC, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import { Transitions } from '@/components/layout/Transitions'
import { useAppDispatch, useAppSelector } from '@/store'
import { commitLocale } from '@/store/reducers/AppSlice'
import { getAppState } from '@/store/selectors/AppSelector'
import { Locale } from '@/types'

import { MainCard } from '../ProfileSection/MainCard'

export const LocalizationSection: FC = () => {
  const { locale } = useAppSelector(getAppState)
  const { t } = useTranslation('', { keyPrefix: 'components.layout.header.localizationSection' })
  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'))
  const dispatch = useAppDispatch()

  const anchorRef = useRef<HTMLDivElement | null>(null)

  const handleListItemClick = (value: Locale) => {
    dispatch(commitLocale(value))
    setOpen(false)
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return
    }
    setOpen(false)
  }

  return (
    <>
      <Box
        sx={{
          ml: 2,
          [theme.breakpoints.down('md')]: {
            ml: 1,
          },
        }}
      >
        <ButtonBase sx={{ borderRadius: '8px' }}>
          <Avatar
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup={'true'}
            color={'inherit'}
            onClick={handleToggle}
            ref={anchorRef}
            sx={{
              width: '35px',
              height: '35px',
              border: '1px solid',
              borderColor: theme.palette.primary.light,
              background: theme.palette.primary.light,
              color: theme.palette.primary.dark,
              transition: 'all .2s ease-in-out',
              '&[aria-controls="menu-list-grow"],&:hover': {
                borderColor: theme.palette.primary.main,
                background: theme.palette.primary.main,
                color: theme.palette.primary.light,
              },
            }}
            variant={'rounded'}
          >
            <Typography color={'inherit'} sx={{ textTransform: 'uppercase' }} variant={'h5'}>
              {locale}
            </Typography>
          </Avatar>
        </ButtonBase>
      </Box>
      <Popper
        anchorEl={anchorRef.current}
        disablePortal
        open={open}
        placement={matchesXs ? 'bottom-start' : 'bottom-start'}
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? 0 : 0, 20],
              },
            },
          ],
        }}
        role={undefined}
        transition
      >
        {({ TransitionProps }) => (
          <Transitions
            direction={'right'}
            in={open}
            position={matchesXs ? 'top-left' : 'top'}
            type={'zoom'}
            {...TransitionProps}
          >
            <Paper sx={{ background: theme.palette.background.default }}>
              <MainCard border={false} content={false} elevation={16} shadow={theme.shadows[2]}>
                <ClickAwayListener onClickAway={handleClose}>
                  <List
                    component={'nav'}
                    sx={{
                      padding: '10px',
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: '8px',
                      minWidth: 200,
                      [theme.breakpoints.down('md')]: {
                        minWidth: '100%',
                      },
                      '& .MuiListItemButton-root': {
                        mt: 0.5,
                      },
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleListItemClick('tr')}
                      selected={locale === 'tr'}
                      sx={{
                        borderRadius: `8px`,
                        marginTop: '0px!important',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Grid container>
                            <Typography color={'textPrimary'}>{t('turkish')}</Typography>
                            <Typography
                              color={'textSecondary'}
                              sx={{ ml: '8px' }}
                              variant={'caption'}
                            >
                              {'TR'}
                            </Typography>
                          </Grid>
                        }
                      />
                    </ListItemButton>
                    <ListItemButton
                      onClick={() => handleListItemClick('en')}
                      selected={locale === 'en'}
                      sx={{
                        borderRadius: `8px`,
                        marginTop: '0px!important',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Grid container>
                            <Typography color={'textPrimary'}>{t('english')}</Typography>
                            <Typography
                              color={'textSecondary'}
                              sx={{ ml: '8px' }}
                              variant={'caption'}
                            >
                              {'EN'}
                            </Typography>
                          </Grid>
                        }
                      />
                    </ListItemButton>
                  </List>
                </ClickAwayListener>
              </MainCard>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  )
}

export default LocalizationSection
